import { useCallback, useState } from 'react';

/**
 * Per-list, per-viewer column widths for the shared `Table` / `DataListShell` (#13249).
 *
 * WHY THIS LIVES IN `packages/ui`, NOT IN THE INVOICES PAGE
 * The resize affordance belongs to the shared shell that ~20 modernised admin lists sit on
 * (Feature #13135), so implementing it here lands it on every list at once with one
 * behaviour. The cost is that this is `shared-space` code: the regression surface is every
 * consumer, which is why `Table` renders byte-identically when no width has been stored.
 *
 * WHY PER LIST, NOT GLOBAL (AC4)
 * Widths are keyed by a *list id*, so resizing the invoices list can never resize accounts.
 * `DataListShell` derives that id from its `title` unless the caller passes an explicit
 * `listId` — see `listIdFromTitle`.
 *
 * WHY `localStorage`
 * A pixel width is a per-viewer convenience, not shared state: it carries no client data
 * (a list id and a number), never reaches another user, and never reaches the API. Every
 * access is wrapped — a private window, cleared site data, or a browser configured to block
 * site data can each make `localStorage` throw on read *or* write, and a column width is
 * never worth breaking a page over. Failure degrades to "no stored widths", which is
 * exactly the AC5 default rendering.
 */

/** Storage-key scheme: `tsp.list.<listId>.columnWidths`, matching the existing
 *  `tsp.deals.forecast.winPct` convention in `pages/Deals/Forecast.tsx`. */
export function columnWidthsStorageKey(listId: string): string {
  return `tsp.list.${listId}.columnWidths`;
}

/** Floor/ceiling for a stored width. The floor keeps a column from being dragged to
 *  invisibility — a hidden column *conceals* a value an operator is relying on (the
 *  security note on #13249), so "resized to nothing" must not be reachable. */
export const COLUMN_WIDTH_MIN_PX = 60;
export const COLUMN_WIDTH_MAX_PX = 900;
/** One ArrowLeft/ArrowRight press (AC6 — keyboard-operable resize). */
export const COLUMN_WIDTH_KEYBOARD_STEP_PX = 16;
/** Used only when a column's real width cannot be measured (jsdom has no layout engine,
 *  so `getBoundingClientRect()` reports 0 there). Never used in a real browser. */
export const COLUMN_WIDTH_FALLBACK_PX = 160;

export type ColumnWidths = Record<string, number>;

export function clampColumnWidth(px: number): number {
  return Math.min(COLUMN_WIDTH_MAX_PX, Math.max(COLUMN_WIDTH_MIN_PX, Math.round(px)));
}

/** Slugify a list title into a stable storage id ("Billing Entities" -> "billing-entities"). */
export function listIdFromTitle(title: string): string {
  const slug = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || 'list';
}

/** Drop anything that isn't a finite in-range number, so a hand-edited or
 *  version-skewed storage entry can never feed a NaN width into a style attribute. */
function sanitise(raw: unknown): ColumnWidths {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: ColumnWidths = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = clampColumnWidth(value);
  }
  return out;
}

export function loadColumnWidths(listId: string): ColumnWidths {
  try {
    const raw = localStorage.getItem(columnWidthsStorageKey(listId));
    if (!raw) return {};
    return sanitise(JSON.parse(raw));
  } catch {
    /* Unreadable or unavailable storage — fall back to natural widths (the AC5 default). */
    return {};
  }
}

export interface UseColumnWidthsResult {
  widths: ColumnWidths;
  /** Replaces the whole map — `Table` computes the next map and hands it over, keeping the
   *  Table itself stateless in exactly the way it already is for sorting. */
  setWidths: (next: ColumnWidths) => void;
  /** Clears every stored width for this list, returning it to its natural layout. */
  resetWidths: () => void;
}

export function useColumnWidths(listId: string): UseColumnWidthsResult {
  const [state, setState] = useState<{ listId: string; widths: ColumnWidths }>(
    () => ({ listId, widths: loadColumnWidths(listId) }),
  );

  // Per-list isolation (AC4): if the same mounted shell switches list identity, reload from
  // THAT list's key instead of carrying the previous list's widths across. This is React's
  // documented "adjust state when a prop changes" pattern — the set during render re-renders
  // immediately without committing the stale value.
  if (state.listId !== listId) {
    setState({ listId, widths: loadColumnWidths(listId) });
  }

  const persist = useCallback((next: ColumnWidths) => {
    const cleaned = sanitise(next);
    setState({ listId, widths: cleaned });
    try {
      localStorage.setItem(columnWidthsStorageKey(listId), JSON.stringify(cleaned));
    } catch {
      /* Storage unavailable — the resize still applies for this session. Non-fatal. */
    }
  }, [listId]);

  const resetWidths = useCallback(() => {
    setState({ listId, widths: {} });
    try {
      localStorage.removeItem(columnWidthsStorageKey(listId));
    } catch {
      /* Non-fatal — see above. */
    }
  }, [listId]);

  return { widths: state.listId === listId ? state.widths : {}, setWidths: persist, resetWidths };
}
